/**
 * Errors a data source raises **after** its write has already been accepted (parity card P-120).
 *
 * The grid's optimistic paint has exactly one rule: a write the server refused is rolled back
 * and reported, a write the server accepted is kept. A source method does more than the call
 * though — it maps the answer onto the grid's row shape — and a bug in *that* half used to
 * arrive at the caller as an ordinary rejected promise, indistinguishable from a refusal. The
 * name cell's rename is where it showed: `items.rename` answers without a `columnValues` map,
 * `toItemRow` threw on it, and the grid rolled a **successful** rename back and toasted
 * *"Cannot convert undefined or null to object"* over a database that already held the new name.
 *
 * So a source that has written wraps everything it does afterwards in this: the caller keeps the
 * write, repaints from the server and reports the bug as a bug.
 */
export class PostWriteError extends Error {
  /** The procedure whose answer could not be read. */
  readonly procedure: string;
  /** Always `true`: the mutation this error came out of *did* happen. */
  readonly written = true;

  constructor(procedure: string, cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'PostWriteError';
    this.procedure = procedure;
    this.cause = cause;
  }
}

export function isPostWriteError(error: unknown): error is PostWriteError {
  return error instanceof PostWriteError;
}
